import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import useToast from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';
import { normalizeRole } from '../utils/roles';

const RESOURCE_LABELS = {
  dashboard: 'Anasayfa', announcements: 'Duyurular', documents: 'Dokümanlar',
  directory: 'Çalışan rehberi', employees: 'Çalışan yönetimi', cafeteria: 'Yemekhane',
  events: 'Etkinlikler', content: 'İçerik yönetimi', companies: 'Firma yönetimi',
  roles: 'Rol yönetimi', reports: 'Raporlar', assets: 'Demirbaş kataloğu', assetRequests: 'Demirbaş talepleri'
};
const EMPLOYEE_FIELD_LABELS = {
  employeeNumber: 'Çalışan numarası',
  identity: 'Ad ve soyad',
  email: 'E-posta',
  contact: 'Telefon ve dahili',
  organization: 'Şirket, bölge, departman',
  position: 'Pozisyon',
  dates: 'İşe başlama ve doğum tarihi',
  office: 'Ofis / lokasyon',
  role: 'Sistem rolü ve özel roller',
  status: 'Aktiflik ve rehber görünürlüğü'
};
const ACTION_LABELS = { view: 'Görüntüle', create: 'Oluştur', edit: 'Düzenle', delete: 'Sil', manage: 'Tam yönetim' };
const emptyRole = { name: '', description: '', color: '#0f5964', position: 0, permissions: [] };

const RoleManagementPage = () => {
  const [roles, setRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [meta, setMeta] = useState({ resources: Object.keys(RESOURCE_LABELS), actions: Object.keys(ACTION_LABELS), employeeFields: Object.keys(EMPLOYEE_FIELD_LABELS) });
  const [selectedId, setSelectedId] = useState('new');
  const [draft, setDraft] = useState(emptyRole);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const { toasts, showToast, removeToast } = useToast();
  const isSuperAdmin = useMemo(() => {
    try {
      return normalizeRole(JSON.parse(localStorage.getItem('userData') || '{}').role) === 'super_admin';
    } catch (error) {
      return false;
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const [roleResponse, userResponse] = await Promise.all([
        api.getRoles(),
        api.getUsers({ limit: 1000, status: 'active', ...(isSuperAdmin ? { allCompanies: '1' } : {}) })
      ]);
      setRoles(roleResponse.data || []);
      setMeta(roleResponse.meta || { resources: Object.keys(RESOURCE_LABELS), actions: Object.keys(ACTION_LABELS) });
      setEmployees(userResponse.users || []);
    } catch (error) {
      showToast('Rol yönetimi verileri alınamadı.', 'error');
    }
  }, [isSuperAdmin, showToast]);

  useEffect(() => { load(); }, [load]);

  const selectedRole = roles.find((role) => role._id === selectedId);
  const assignedIds = useMemo(() => new Set(
    employees.filter((employee) => (employee.customRoles || []).some((role) => (role._id || role) === selectedId)).map((employee) => employee._id)
  ), [employees, selectedId]);
  const visibleEmployees = employees.filter((employee) => (
    `${employee.firstName} ${employee.lastName} ${employee.email} ${employee.company || ''} ${employee.department || ''}`
      .toLocaleLowerCase('tr-TR')
      .includes(search.toLocaleLowerCase('tr-TR'))
  ));

  const selectRole = (role) => {
    setSelectedId(role?._id || 'new');
    setDraft(role ? { ...role, permissions: role.permissions || [] } : emptyRole);
  };

  const toggleEmployeeField = (field) => {
    setDraft((current) => {
      const row = current.permissions.find((permission) => permission.resource === 'employees') || { resource: 'employees', actions: ['edit'], fields: [] };
      const fields = row.fields || [];
      const nextFields = fields.includes(field) ? fields.filter((item) => item !== field) : [...fields, field];
      const actions = row.actions?.includes('edit') || row.actions?.includes('manage') ? row.actions : [...(row.actions || []), 'edit'];
      const remaining = current.permissions.filter((permission) => permission.resource !== 'employees');
      return { ...current, permissions: [...remaining, { ...row, actions, fields: nextFields }] };
    });
  };

  const togglePermission = (resource, action) => {
    setDraft((current) => {
      const row = current.permissions.find((permission) => permission.resource === resource);
      const actions = row?.actions || [];
      const nextActions = actions.includes(action) ? actions.filter((item) => item !== action) : [...actions, action];
      const remaining = current.permissions.filter((permission) => permission.resource !== resource);
      return { ...current, permissions: nextActions.length ? [...remaining, { resource, actions: nextActions }] : remaining };
    });
  };

  const saveRole = async () => {
    if (!draft.name.trim()) return showToast('Rol adı zorunludur.', 'error');
    setSaving(true);
    try {
      const response = selectedRole ? await api.updateRole(selectedRole._id, draft) : await api.createRole(draft);
      showToast(response.message, 'success');
      await load();
      setSelectedId(response.data._id);
      setDraft(response.data);
    } catch (error) {
      showToast(error.message || 'Rol kaydedilemedi.', 'error');
    } finally { setSaving(false); }
  };

  const removeRole = async () => {
    if (!selectedRole || !window.confirm(`“${selectedRole.name}” rolünü silmek istediğinize emin misiniz?`)) return;
    try {
      await api.deleteRole(selectedRole._id);
      showToast('Rol silindi.', 'success');
      selectRole(null);
      await load();
    } catch (error) { showToast(error.message, 'error'); }
  };

  const toggleMember = async (employee) => {
    const currentIds = (employee.customRoles || []).map((role) => role._id || role);
    const nextIds = currentIds.includes(selectedId) ? currentIds.filter((id) => id !== selectedId) : [...currentIds, selectedId];
    try {
      await api.assignUserRoles(employee._id, nextIds);
      setEmployees((current) => current.map((item) => item._id === employee._id ? { ...item, customRoles: nextIds } : item));
    } catch (error) { showToast('Çalışan rolü güncellenemedi.', 'error'); }
  };

  return (
    <div className="role-page">
      <header className="role-page-header">
        <div><h1>Rol yönetimi</h1><p>Yetkileri görev bazında tanımlayın ve çalışanlara birden fazla rol verin.</p></div>
        <button className="role-primary-button" onClick={() => selectRole(null)}><i className="bx bx-plus" /> Yeni rol</button>
      </header>
      <div className="role-workspace">
        <aside className="role-list" aria-label="Roller">
          <div className="role-list-title"><span>Roller</span><small>{roles.length}</small></div>
          {roles.map((role) => (
            <button key={role._id} className={`role-list-item ${selectedId === role._id ? 'active' : ''}`} onClick={() => selectRole(role)}>
              <span className="role-dot" style={{ background: role.color }} /><span><strong>{role.name}</strong><small>{role.memberCount} çalışan</small></span>
            </button>
          ))}
        </aside>
        <main className="role-editor">
          <div className="role-editor-head">
            <div><h2>{selectedRole ? selectedRole.name : 'Yeni rol'}</h2><p>Rol görünümünü, sırasını ve erişebileceği işlemleri belirleyin.</p></div>
            <div className="role-editor-actions">{selectedRole && <button className="role-danger-button" onClick={removeRole}>Rolü sil</button>}<button className="role-primary-button" onClick={saveRole} disabled={saving}>{saving ? 'Kaydediliyor…' : 'Değişiklikleri kaydet'}</button></div>
          </div>
          <section className="role-section">
            <h3>Rol bilgileri</h3>
            <div className="role-form-grid">
              <label><span>Rol adı</span><input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></label>
              <label><span>Renk</span><div className="role-color-field"><input type="color" value={draft.color} onChange={(e) => setDraft({ ...draft, color: e.target.value })} /><code>{draft.color}</code></div></label>
              <label className="role-field-wide"><span>Açıklama</span><textarea rows="2" value={draft.description || ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></label>
              <label><span>Hiyerarşi sırası</span><input type="number" value={draft.position || 0} onChange={(e) => setDraft({ ...draft, position: Number(e.target.value) })} /></label>
            </div>
          </section>
          <section className="role-section">
            <div className="role-section-heading"><div><h3>Yetkiler</h3><p>Her modül için izinleri ayrı ayrı açın.</p></div></div>
            <div className="permission-table-wrap"><table className="permission-table"><thead><tr><th>Modül</th>{meta.actions.map((action) => <th key={action}>{ACTION_LABELS[action]}</th>)}</tr></thead><tbody>{meta.resources.map((resource) => { const actions = draft.permissions.find((item) => item.resource === resource)?.actions || []; return <tr key={resource}><td>{RESOURCE_LABELS[resource] || resource}</td>{meta.actions.map((action) => <td key={action}><input aria-label={`${RESOURCE_LABELS[resource] || resource} ${ACTION_LABELS[action]}`} type="checkbox" checked={actions.includes(action)} onChange={() => togglePermission(resource, action)} /></td>)}</tr>; })}</tbody></table></div>
            <div className="mt-4">
              <h4>Çalışan düzenleme alanları</h4>
              <p>Düzenle yetkisi olan bir rol, yalnızca işaretlenen alanları değiştirebilir. Hiçbiri işaretli değilse tüm alanlar açıktır.</p>
              <div className="d-flex flex-wrap gap-3">
                {(meta.employeeFields || Object.keys(EMPLOYEE_FIELD_LABELS)).map((field) => {
                  const selected = draft.permissions.find((item) => item.resource === 'employees')?.fields || [];
                  return <label key={field} className="form-check"><input className="form-check-input" type="checkbox" checked={selected.includes(field)} onChange={() => toggleEmployeeField(field)} /><span className="form-check-label">{EMPLOYEE_FIELD_LABELS[field] || field}</span></label>;
                })}
              </div>
            </div>
          </section>
          {selectedRole && <section className="role-section"><div className="role-section-heading"><div><h3>Çalışanlar</h3><p>{assignedIds.size} çalışan bu role sahip.{isSuperAdmin ? ' Tüm şirketlerde arama yapabilirsiniz.' : ''}</p></div><div className="role-member-search"><i className="bx bx-search" /><input placeholder="Ad, e-posta, şirket veya departman ara" value={search} onChange={(e) => setSearch(e.target.value)} /></div></div><div className="role-members">{visibleEmployees.map((employee) => <label key={employee._id} className="role-member"><span className="role-member-avatar">{employee.firstName?.[0]}{employee.lastName?.[0]}</span><span><strong>{employee.firstName} {employee.lastName}</strong><small>{[employee.company, employee.department, employee.email].filter(Boolean).join(' · ')}</small></span><input type="checkbox" checked={assignedIds.has(employee._id)} onChange={() => toggleMember(employee)} /></label>)}</div></section>}
        </main>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default RoleManagementPage;
